import type {
  FloraCategory,
  FloraDefinition,
  MoistureRequirement,
  SunlightRequirement,
} from '@/flora/FloraDefinition';
import type { FloraResolver } from '@/localization/FloraResolver';
import {
  tallySuitability,
  type NativeStatus,
  type SuitabilityRating,
  type SuitabilityTally,
} from '@/localization/Suitability';

/**
 * Pure faceting, filtering, sorting, and grouping for the flora palette (spec §19, milestone 6.4).
 * Deliberately free of DOM and Three.js so the browse model is unit-testable on its own — the panel
 * in `FloraBrowser.ts` only renders what these functions return.
 *
 * Facets are *derived* from the catalog rather than stored on it: form factor comes from the
 * geometry archetype, genus from the botanical binomial, and colour families from the seasonal
 * palette. That keeps `flora.json` a botanical record instead of a UI configuration file.
 */

/* ---------------------------------- Form factor ---------------------------------- */

export type FormFactor = 'tree' | 'shrub' | 'grass' | 'perennial' | 'groundcover';

export const FORM_FACTORS: readonly FormFactor[] = ['tree', 'shrub', 'grass', 'perennial', 'groundcover'];

const FORM_FACTOR_LABELS: Record<FormFactor, string> = {
  tree: 'Trees',
  shrub: 'Shrubs',
  grass: 'Grasses',
  perennial: 'Perennials',
  groundcover: 'Groundcover',
};

export function formFactorLabel(value: FormFactor): string {
  return FORM_FACTOR_LABELS[value];
}

/** The broad structural family a plant belongs to — the palette's primary axis. */
export function formFactorOf(definition: FloraDefinition): FormFactor {
  switch (definition.category) {
    case 'deciduous-tree':
    case 'evergreen-tree':
      return 'tree';
    case 'deciduous-shrub':
    case 'evergreen-shrub':
      return 'shrub';
    case 'ornamental-grass':
      return 'grass';
    case 'perennial':
    case 'annual':
      return 'perennial';
    case 'groundcover':
      return 'groundcover';
  }
}

/* ------------------------------------ Genus ------------------------------------- */

/** Genus from the botanical binomial — `Acer rubrum` → `Acer`. */
export function genusOf(definition: FloraDefinition): string {
  const [genus] = definition.botanicalName.trim().split(/\s+/);
  return genus ?? definition.botanicalName;
}

/** Species epithet — `Acer rubrum` → `rubrum`. Empty when the name has no second term. */
export function speciesEpithetOf(definition: FloraDefinition): string {
  const parts = definition.botanicalName.trim().split(/\s+/);
  return parts[1] ?? '';
}

/* --------------------------------- Colour family -------------------------------- */

export type ColorFamily = 'green' | 'blue-green' | 'gold' | 'orange' | 'red' | 'purple' | 'white';

export const COLOR_FAMILIES: readonly ColorFamily[] = [
  'green',
  'blue-green',
  'gold',
  'orange',
  'red',
  'purple',
  'white',
];

const COLOR_FAMILY_LABELS: Record<ColorFamily, string> = {
  green: 'Green',
  'blue-green': 'Blue-green',
  gold: 'Gold',
  orange: 'Orange',
  red: 'Red',
  purple: 'Purple',
  white: 'White & cream',
};

/** Representative swatch for each family, used for the filter chips. */
export const COLOR_FAMILY_SWATCHES: Record<ColorFamily, string> = {
  green: '#4c8342',
  'blue-green': '#5a8fa0',
  gold: '#d9b23f',
  orange: '#dd7a2c',
  red: '#c33a25',
  purple: '#a05fb4',
  white: '#f0eee2',
};

export function colorFamilyLabel(value: ColorFamily): string {
  return COLOR_FAMILY_LABELS[value];
}

interface Hsl {
  h: number;
  s: number;
  l: number;
}

/** Convert `#rrggbb` (or `#rgb`) to HSL with h in degrees and s/l in [0, 1]. */
export function hexToHsl(hex: string): Hsl {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean.padEnd(6, '0').slice(0, 6);
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const delta = max - min;
  if (delta === 0) return { h: 0, s: 0, l };

  const s = delta / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = 60 * (((g - b) / delta) % 6);
  else if (max === g) h = 60 * ((b - r) / delta + 2);
  else h = 60 * ((r - g) / delta + 4);
  return { h: (h + 360) % 360, s, l };
}

/** Bucket a hex colour into a perceptual family the palette can filter on. */
export function colorFamilyOf(hex: string): ColorFamily {
  const { h, s, l } = hexToHsl(hex);
  // Blossom whites are rarely neutral — cream and blush sit near 90% lightness with real
  // saturation, so lightness has to win over hue before the warm-hue rules below see them.
  if (l > 0.86) return 'white';
  if (l > 0.78 && s < 0.35) return 'white';
  if (s < 0.1) return 'white';
  if (h >= 150 && h < 250) return 'blue-green';
  if (h >= 75 && h < 150) return 'green';
  if (h >= 50 && h < 75) return 'gold';
  if (h >= 25 && h < 50) return l > 0.55 ? 'gold' : 'orange';
  if (h >= 250 && h < 335) return 'purple';
  // Warm reds wrap around 0°; a dark, desaturated one reads as bronze/orange in a planting plan.
  return s < 0.45 && l < 0.45 ? 'orange' : 'red';
}

/** Every colour a plant offers across the year, foliage and bloom alike. */
export function seasonalColors(definition: FloraDefinition): string[] {
  const { seasons } = definition;
  const colors: string[] = [];
  for (const key of ['spring', 'summer', 'autumn', 'winter'] as const) {
    const appearance = seasons[key];
    if (appearance.foliageDensity > 0.1) colors.push(appearance.foliageColor);
    if (appearance.flowerColor && (appearance.flowerDensity ?? 0) > 0.1) colors.push(appearance.flowerColor);
    if (appearance.fruitColor && (appearance.fruitDensity ?? 0) > 0.1) colors.push(appearance.fruitColor);
  }
  for (const window of seasons.bloomWindows ?? []) colors.push(window.color);
  return colors;
}

/** Distinct colour families a plant contributes across the year. */
export function colorFamiliesOf(definition: FloraDefinition): ColorFamily[] {
  const families = new Set<ColorFamily>();
  for (const color of seasonalColors(definition)) families.add(colorFamilyOf(color));
  return COLOR_FAMILIES.filter((family) => families.has(family));
}

/* ------------------------------------ Filter ------------------------------------ */

export interface FloraFilter {
  search: string;
  formFactors: FormFactor[];
  categories: FloraCategory[];
  colorFamilies: ColorFamily[];
  genera: string[];
  sunlight: SunlightRequirement[];
  moisture: MoistureRequirement[];
  tags: string[];
  favoritesOnly: boolean;
  /** Upper bound on mature typical height, in meters. `null` = no limit. */
  maxHeightMeters: number | null;
  /**
   * Narrow to plants this site actually suits. `[]` shows everything, which is the default and
   * stays the default after localizing: the point of a suitability rating is to inform a choice, not
   * to make it, and a palette that hides two hundred plants the moment a ZIP is typed would be
   * deciding for the designer. See `localization/Suitability.ts`.
   */
  suitability: SuitabilityRating[];
  /** Narrow to plants native to (or at least not invasive in) this site's region. */
  nativeOnly: boolean;
}

export function defaultFloraFilter(): FloraFilter {
  return {
    search: '',
    formFactors: [],
    categories: [],
    colorFamilies: [],
    genera: [],
    sunlight: [],
    moisture: [],
    tags: [],
    favoritesOnly: false,
    maxHeightMeters: null,
    suitability: [],
    nativeOnly: false,
  };
}

/** True when no filter is narrowing the catalog. */
export function isFilterEmpty(filter: FloraFilter): boolean {
  return activeFilterCount(filter) === 0;
}

/** Number of active filter facets, for the "clear filters (n)" affordance. */
export function activeFilterCount(filter: FloraFilter): number {
  return (
    (filter.search.trim() === '' ? 0 : 1) +
    filter.formFactors.length +
    filter.categories.length +
    filter.colorFamilies.length +
    filter.genera.length +
    filter.sunlight.length +
    filter.moisture.length +
    filter.tags.length +
    (filter.favoritesOnly ? 1 : 0) +
    (filter.maxHeightMeters === null ? 0 : 1) +
    // One, however many ratings are ticked: "best for this site" is a single idea to the person
    // using it, and counting it as three would make the badge read as three filters to clear.
    (filter.suitability.length === 0 ? 0 : 1) +
    (filter.nativeOnly ? 1 : 0)
  );
}

/** The text a search query is matched against. */
export function searchCorpus(definition: FloraDefinition): string {
  return [
    definition.botanicalName,
    definition.cultivar ?? '',
    ...definition.commonNames,
    ...definition.tags,
    definition.category,
  ]
    .join(' ')
    .toLowerCase();
}

export interface FilterContext {
  favorites?: ReadonlySet<string>;
  /**
   * The site's resolver, when there is one.
   *
   * Passed in rather than imported so this module stays free of application state, and optional so a
   * palette on an unlocalized site behaves exactly as it always did. Suitability and native status
   * are *asked for*, never computed here — there is one place those are decided.
   */
  resolver?: FloraResolver;
}

/** The site's rating for a plant, or undefined when the site has not been localized. */
export function ratingOf(
  definition: FloraDefinition,
  context: FilterContext,
): SuitabilityRating | undefined {
  const resolved = context.resolver?.resolve(definition);
  return resolved?.suitability.localized === true ? resolved.rating : undefined;
}

/** The site's native status for a plant, or undefined when unlocalized. */
export function nativeStatusOf(
  definition: FloraDefinition,
  context: FilterContext,
): NativeStatus | undefined {
  const resolved = context.resolver?.resolve(definition);
  return resolved?.suitability.localized === true ? resolved.nativeStatus : undefined;
}

/** The palette's `642 Ideal or suitable · 74 Marginal · 31 Outside range` line. */
export function tallyForSite(
  definitions: readonly FloraDefinition[],
  context: FilterContext,
): SuitabilityTally | null {
  if (!context.resolver) return null;
  const ratings: SuitabilityRating[] = [];
  for (const definition of definitions) {
    const rating = ratingOf(definition, context);
    if (rating === undefined) return null;
    ratings.push(rating);
  }
  return tallySuitability(ratings);
}

/** Facets are AND-ed with each other and OR-ed within themselves — the usual catalog convention. */
export function matchesFilter(
  definition: FloraDefinition,
  filter: FloraFilter,
  context: FilterContext = {},
): boolean {
  const query = filter.search.trim().toLowerCase();
  if (query !== '') {
    const corpus = searchCorpus(definition);
    if (!query.split(/\s+/).every((term) => corpus.includes(term))) return false;
  }
  if (filter.favoritesOnly && !(context.favorites?.has(definition.id) ?? false)) return false;
  if (filter.formFactors.length > 0 && !filter.formFactors.includes(formFactorOf(definition))) return false;
  if (filter.categories.length > 0 && !filter.categories.includes(definition.category)) return false;
  if (filter.genera.length > 0 && !filter.genera.includes(genusOf(definition))) return false;

  if (filter.colorFamilies.length > 0) {
    const families = colorFamiliesOf(definition);
    if (!filter.colorFamilies.some((family) => families.includes(family))) return false;
  }
  if (filter.sunlight.length > 0 && !filter.sunlight.some((s) => definition.sunlight.includes(s))) {
    return false;
  }
  if (filter.moisture.length > 0 && !filter.moisture.some((m) => definition.moisture.includes(m))) {
    return false;
  }
  if (filter.tags.length > 0 && !filter.tags.every((tag) => definition.tags.includes(tag))) return false;
  if (
    filter.maxHeightMeters !== null &&
    definition.growth.matureHeightMeters.typical > filter.maxHeightMeters
  ) {
    return false;
  }

  // Localization facets are inert without a profile. A site that has not said where it is has not
  // ruled anything out, so these must not quietly empty the palette.
  if (filter.suitability.length > 0) {
    const rating = ratingOf(definition, context);
    if (rating !== undefined && !filter.suitability.includes(rating)) return false;
  }
  if (filter.nativeOnly) {
    const status = nativeStatusOf(definition, context);
    if (status !== undefined && status !== 'native') return false;
  }
  return true;
}

export function filterFlora(
  definitions: readonly FloraDefinition[],
  filter: FloraFilter,
  context: FilterContext = {},
): FloraDefinition[] {
  return definitions.filter((definition) => matchesFilter(definition, filter, context));
}

/* ------------------------------- Sorting & grouping ------------------------------ */

export type FloraSort = 'common-name' | 'botanical-name' | 'height' | 'genus' | 'suitability';

export const FLORA_SORTS: readonly { value: FloraSort; label: string }[] = [
  { value: 'common-name', label: 'Common name' },
  { value: 'botanical-name', label: 'Botanical name' },
  { value: 'height', label: 'Mature height' },
  { value: 'genus', label: 'Genus' },
  { value: 'suitability', label: 'Best for this site' },
];

/** Sorts that need a localized site to mean anything, and are hidden without one. */
export function sortNeedsLocalization(sort: FloraSort): boolean {
  return sort === 'suitability';
}

export function displayName(definition: FloraDefinition): string {
  return definition.commonNames[0] ?? definition.botanicalName;
}

/** Full display name including cultivar, e.g. `Panicle hydrangea 'Limelight'`. */
export function fullDisplayName(definition: FloraDefinition): string {
  const base = displayName(definition);
  return definition.cultivar ? `${base} '${definition.cultivar}'` : base;
}

const SUITABILITY_ORDER: Record<SuitabilityRating, number> = {
  ideal: 0,
  suitable: 1,
  marginal: 2,
  'outside-range': 3,
};

export function sortFlora(
  definitions: readonly FloraDefinition[],
  sort: FloraSort,
  context: FilterContext = {},
): FloraDefinition[] {
  const sorted = [...definitions];
  sorted.sort((a, b) => {
    switch (sort) {
      case 'botanical-name':
        return a.botanicalName.localeCompare(b.botanicalName);
      case 'height':
        return a.growth.matureHeightMeters.typical - b.growth.matureHeightMeters.typical;
      case 'genus': {
        const byGenus = genusOf(a).localeCompare(genusOf(b));
        return byGenus !== 0 ? byGenus : a.botanicalName.localeCompare(b.botanicalName);
      }
      case 'suitability': {
        // Unrated plants sort as `suitable` rather than to the bottom: an unlocalized site has not
        // judged them, and burying them would present "not checked" as "not recommended".
        const ra = SUITABILITY_ORDER[ratingOf(a, context) ?? 'suitable'];
        const rb = SUITABILITY_ORDER[ratingOf(b, context) ?? 'suitable'];
        return ra !== rb ? ra - rb : displayName(a).localeCompare(displayName(b));
      }
      case 'common-name':
        return displayName(a).localeCompare(displayName(b));
    }
  });
  return sorted;
}

export type FloraGroupBy = 'none' | 'form-factor' | 'category' | 'genus' | 'color';

export const FLORA_GROUPINGS: readonly { value: FloraGroupBy; label: string }[] = [
  { value: 'form-factor', label: 'Form factor' },
  { value: 'category', label: 'Category' },
  { value: 'genus', label: 'Genus' },
  { value: 'color', label: 'Colour' },
  { value: 'none', label: 'Ungrouped' },
];

export interface FloraGroup {
  key: string;
  label: string;
  items: FloraDefinition[];
}

/** Human label for a category id, e.g. `deciduous-tree` → `Deciduous tree`. */
export function categoryLabel(category: FloraCategory): string {
  const words = category.split('-');
  const [first, ...rest] = words;
  if (!first) return category;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

/**
 * Partition the (already filtered and sorted) list into palette sections. Groups appear in the
 * order their first member does, so the active sort also determines section order.
 */
export function groupFlora(definitions: readonly FloraDefinition[], groupBy: FloraGroupBy): FloraGroup[] {
  if (groupBy === 'none') {
    return [{ key: 'all', label: 'All plants', items: [...definitions] }];
  }

  const groups = new Map<string, FloraGroup>();
  for (const definition of definitions) {
    for (const { key, label } of groupKeysFor(definition, groupBy)) {
      const existing = groups.get(key);
      if (existing) existing.items.push(definition);
      else groups.set(key, { key, label, items: [definition] });
    }
  }
  return [...groups.values()];
}

function groupKeysFor(
  definition: FloraDefinition,
  groupBy: Exclude<FloraGroupBy, 'none'>,
): { key: string; label: string }[] {
  switch (groupBy) {
    case 'form-factor': {
      const form = formFactorOf(definition);
      return [{ key: form, label: formFactorLabel(form) }];
    }
    case 'category':
      return [{ key: definition.category, label: categoryLabel(definition.category) }];
    case 'genus': {
      const genus = genusOf(definition);
      return [{ key: genus, label: genus }];
    }
    case 'color':
      // A plant with autumn colour legitimately belongs to more than one colour section.
      return colorFamiliesOf(definition).map((family) => ({ key: family, label: colorFamilyLabel(family) }));
  }
}

/* -------------------------------- Facet counting -------------------------------- */

export interface FacetOption<T extends string> {
  value: T;
  label: string;
  count: number;
}

/**
 * Count how many definitions fall under each value of a facet. Used to label the filter chips and to
 * grey out facets that would produce an empty result.
 */
export function facetCounts<T extends string>(
  definitions: readonly FloraDefinition[],
  valuesOf: (definition: FloraDefinition) => readonly T[],
  labelOf: (value: T) => string,
  order?: readonly T[],
): FacetOption<T>[] {
  const counts = new Map<T, number>();
  for (const definition of definitions) {
    for (const value of new Set(valuesOf(definition))) {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  }
  const keys = order ? order.filter((value) => counts.has(value)) : [...counts.keys()].sort();
  return keys.map((value) => ({ value, label: labelOf(value), count: counts.get(value) ?? 0 }));
}

/** All tags in the catalog with their frequency, most common first. */
export function tagFacets(definitions: readonly FloraDefinition[]): FacetOption<string>[] {
  return facetCounts(definitions, (d) => d.tags, (t) => t.replace(/-/g, ' ')).sort(
    (a, b) => b.count - a.count || a.value.localeCompare(b.value),
  );
}

/** All genera in the catalog, alphabetical. */
export function genusFacets(definitions: readonly FloraDefinition[]): FacetOption<string>[] {
  return facetCounts(definitions, (d) => [genusOf(d)], (g) => g);
}

/** Toggle a value in a facet array, returning a new array (filters are treated as immutable). */
export function toggleFacet<T>(values: readonly T[], value: T): T[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}
